import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { SelectChip } from '@cbhq/cds-web/alpha/select-chip/SelectChip';
import { SearchInput } from '@cbhq/cds-web/controls/SearchInput';
import { Box } from '@cbhq/cds-web/layout/Box';
import { HStack } from '@cbhq/cds-web/layout/HStack';

import { workspaceSelectChipOverrides } from '../../../components/selectStyles';
import type { Column, StatusKey } from '../types';

const messages = defineMessages({
  searchPlaceholder: {
    id: 'teamFilters.searchPlaceholder',
    defaultMessage: 'Search',
    description: 'Placeholder text for the member search input',
  },
  anyStatus: {
    id: 'teamFilters.anyStatus',
    defaultMessage: 'Any status',
    description: 'Default label for the status filter chip when no status is selected',
  },
  anyProduct: {
    id: 'teamFilters.anyProduct',
    defaultMessage: 'Any product',
    description: 'Default label for the product filter chip when no product is selected',
  },
  anyEntity: {
    id: 'teamFilters.anyEntity',
    defaultMessage: 'Any entity',
    description: 'Default label for the entity filter chip when no entity is selected',
  },
  clearAll: {
    id: 'teamFilters.clearAll',
    defaultMessage: 'Clear all',
    description: 'Label for the clear-all button in multi-select filter chips',
  },
  filterByStatus: {
    id: 'teamFilters.filterByStatus',
    defaultMessage: 'Filter by status',
    description: 'Accessibility label for the status filter chip',
  },
  filterByProduct: {
    id: 'teamFilters.filterByProduct',
    defaultMessage: 'Filter by product',
    description: 'Accessibility label for the product filter chip',
  },
  filterByEntity: {
    id: 'teamFilters.filterByEntity',
    defaultMessage: 'Filter by entity',
    description: 'Accessibility label for the entity filter chip',
  },
  statusActive: {
    id: 'teamFilters.statusActive',
    defaultMessage: 'Active',
    description: 'Status filter option for active members',
  },
  statusPending: {
    id: 'teamFilters.statusPending',
    defaultMessage: 'Invite pending',
    description: 'Status filter option for members with a pending invite',
  },
});

export type TeamFiltersProps = {
  /** Visible columns — drives which filter chips are shown. */
  columns: Column[];

  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;

  // Status filter
  statusFilter: StatusKey[];
  onStatusFilterChange: (value: StatusKey | StatusKey[] | null) => void;
  availableStatuses: Set<'active' | 'pending'>;

  // Product filter
  productFilter: string[];
  onProductFilterChange: (value: string | string[] | null) => void;
  products: { id: string; label: string }[];

  // Entity filter
  entityFilter: string[];
  onEntityFilterChange: (value: string | string[] | null) => void;
  entities: string[];
};

export function TeamFilters({
  columns,
  searchValue,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  availableStatuses,
  productFilter,
  onProductFilterChange,
  products,
  entityFilter,
  onEntityFilterChange,
  entities,
}: TeamFiltersProps) {
  const { formatMessage } = useIntl();

  const showProductFilter = columns.includes('product') && products.length > 0;
  const showEntityFilter = columns.includes('entity') && entities.length > 0;
  const showStatusFilter = columns.includes('status');

  const statusOptions = useMemo(
    (): { value: StatusKey; label: string; disabled: boolean }[] => [
      {
        value: 'active',
        label: formatMessage(messages.statusActive),
        disabled: !availableStatuses.has('active'),
      },
      {
        value: 'pending',
        label: formatMessage(messages.statusPending),
        disabled: !availableStatuses.has('pending'),
      },
    ],
    [availableStatuses, formatMessage],
  );

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        value: product.id,
        label: product.label,
      })),
    [products],
  );

  const entityOptions = useMemo(
    () =>
      entities.map((entity) => ({
        value: entity,
        label: entity,
      })),
    [entities],
  );

  const clearAllLabel = formatMessage(messages.clearAll);

  return (
    <HStack gap={3} alignItems="center">
      <Box flexGrow={1} maxWidth={240}>
        <SearchInput
          testID="team-member-search-input"
          value={searchValue}
          onChangeText={onSearchChange}
          placeholder={formatMessage(messages.searchPlaceholder)}
          compact
        />
      </Box>

      {showProductFilter && (
        <SelectChip<'multi', string>
          type="multi"
          {...workspaceSelectChipOverrides}
          value={productFilter}
          onChange={onProductFilterChange}
          label={formatMessage(messages.anyProduct)}
          controlAccessibilityLabel={formatMessage(messages.filterByProduct)}
          options={productOptions}
          clearAllLabel={clearAllLabel}
        />
      )}

      {showEntityFilter && (
        <SelectChip<'multi', string>
          type="multi"
          {...workspaceSelectChipOverrides}
          value={entityFilter}
          onChange={onEntityFilterChange}
          label={formatMessage(messages.anyEntity)}
          controlAccessibilityLabel={formatMessage(messages.filterByEntity)}
          options={entityOptions}
          clearAllLabel={clearAllLabel}
        />
      )}

      {showStatusFilter && (
        <SelectChip<'multi', StatusKey>
          type="multi"
          {...workspaceSelectChipOverrides}
          value={statusFilter}
          onChange={onStatusFilterChange}
          label={formatMessage(messages.anyStatus)}
          controlAccessibilityLabel={formatMessage(messages.filterByStatus)}
          options={statusOptions}
          clearAllLabel={clearAllLabel}
        />
      )}
    </HStack>
  );
}
